import { bridge } from '~/services/bridge'
import { collectArchiveFiles } from '~/services/bridge/archiveUtils'
import { shell } from '~/services/bridge/thinShellClient'
import { getPluginAccessToken } from '~/services/bridge/pluginAuth'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage } from '~/utils/shared'

// ============================================================================
// Types
// ============================================================================

export interface Project {
  name: string
  path: string
  type: 'mule' | 'unknown'
}

export interface UploadResult {
  success: boolean
  s3Path?: string
  fileCount?: number
  size?: number
  error?: string
}

export interface DiffEntry {
  diffType: 'MODIFY' | 'ADD' | 'DELETE'
  aFilePath: string
  bFilePath: string
  aFileContent: string
  bFileContent: string
}

export interface ProjectLookupDebugInfo {
  source: 'bootstrap' | 'unified-bridge' | 'eclipse-bridge' | 'eclipse-call' | 'none'
  reason: string
  projectCount: number
}

// ============================================================================
// Presigned Upload Helpers
// ============================================================================

interface PresignedUrlResponse {
  url: string
  fields: Record<string, string>
  file_path: string
}

async function getPresignedUrl(
  endpoint: string,
  filename: string,
  fileSize: number
): Promise<PresignedUrlResponse> {
  return fetchWrapper.post<PresignedUrlResponse>(endpoint, {
    filename,
    file_size: fileSize
  })
}

async function uploadToS3(file: File | Blob, presignedUrl: PresignedUrlResponse): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()

    for (const [key, value] of Object.entries(presignedUrl.fields)) {
      formData.append(key, value)
    }
    formData.append('file', file)

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 upload failed with status: ${xhr.status}`))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

    xhr.open('POST', presignedUrl.url)
    xhr.send(formData)
  })
}

async function notifyUploadComplete(endpoint: string, filePath: string): Promise<string> {
  const response = await fetchWrapper(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_path: filePath })
  })

  if (!response.ok) {
    throw new Error(`Upload complete notification failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    const result = (await response.json()) as Record<string, string>
    return result.path ?? filePath
  }

  const decoder = new TextDecoder()
  let finalPath = filePath

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    const chunk = decoder.decode(value)
    const lines = chunk.split('\n').filter(l => l.trim())
    for (const line of lines) {
      try {
        const data = JSON.parse(line) as Record<string, unknown>
        if (data.error) throw new Error(data.error as string)
        if (data.status === 'completed' || data.status === 'scan_complete') {
          finalPath = (data.path as string) ?? filePath
          reader.cancel()
          return finalPath
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue
        throw e
      }
    }
  }

  return finalPath
}

// ============================================================================
// Project Service
// ============================================================================

class ProjectService {
  private lastLookupDebugInfo: ProjectLookupDebugInfo = {
    source: 'none',
    reason: 'not-started',
    projectCount: 0
  }

  getLastLookupDebugInfo(): ProjectLookupDebugInfo {
    return this.lastLookupDebugInfo
  }

  private setLookupDebugInfo(info: ProjectLookupDebugInfo): void {
    this.lastLookupDebugInfo = info
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
    return Promise.race<T | null>([
      promise,
      new Promise<null>(resolve => {
        setTimeout(() => resolve(null), timeoutMs)
      })
    ])
  }

  private toProject(name?: string, path?: string): Project | null {
    const resolvedPath = path || ''
    if (!resolvedPath) return null
    const inferredName = resolvedPath.split(/[\\/]/).filter(Boolean).pop() || 'Workspace'
    return {
      name: name || inferredName,
      path: resolvedPath,
      type: 'unknown'
    }
  }

  private projectsFromMetaShape(meta: {
    projects?: Array<{ name?: string; path?: string }>
    workspaceFolders?: Array<{ name?: string; path?: string }>
    workspacePaths?: string[]
    workspacePath?: string
    name?: string
  }): Project[] {
    const workspacePaths = [
      ...(meta.workspacePaths || []),
      ...(meta.workspacePath ? [meta.workspacePath] : [])
    ]
    const projectEntries = [
      ...(meta.projects || []),
      ...(meta.workspaceFolders || []),
      ...workspacePaths.map(path => ({ path }))
    ]

    const dedupedByPath = new Map<string, Project>()
    for (const entry of projectEntries) {
      const project = this.toProject(entry.name, entry.path)
      if (project) {
        dedupedByPath.set(project.path, project)
      }
    }

    const projects = Array.from(dedupedByPath.values())
    if (projects.length > 0) return projects

    const rootOnlyProject = this.toProject(
      meta.name,
      meta.workspacePath || meta.workspacePaths?.[0]
    )
    return rootOnlyProject ? [rootOnlyProject] : []
  }

  async getProjects(): Promise<Project[]> {
    // Fastest and most reliable source in VSCode/Cursor:
    // host-injected workspace paths available at webview bootstrap.
    const injectedWorkspacePaths = Array.isArray(window.CURIETECH_WORKSPACE_PATHS)
      ? window.CURIETECH_WORKSPACE_PATHS.filter(Boolean)
      : []
    if (injectedWorkspacePaths.length > 0) {
      const projects = this.projectsFromMetaShape({
        workspacePaths: injectedWorkspacePaths
      })
      this.setLookupDebugInfo({
        source: 'bootstrap',
        reason: 'workspace-paths',
        projectCount: projects.length
      })
      return projects
    }

    // Primary path: unified bridge (normalized in bridge.getWorkspaceInfo)
    const result = await this.withTimeout(bridge.getWorkspaceInfo(), 4000)
    if (result?.success && result.data) {
      const projects = (result.data.projects || [])
        .map(project => this.toProject(project.name, project.path || result.data!.rootPath))
        .filter((project): project is Project => Boolean(project))

      if (projects.length > 0) {
        this.setLookupDebugInfo({
          source: 'unified-bridge',
          reason: 'ok',
          projectCount: projects.length
        })
        return projects
      }

      const rootOnlyProject = this.toProject(result.data.name, result.data.rootPath)
      if (rootOnlyProject) {
        this.setLookupDebugInfo({
          source: 'unified-bridge',
          reason: 'root-path-fallback',
          projectCount: 1
        })
        return [rootOnlyProject]
      }
    } else if (result == null) {
      this.setLookupDebugInfo({
        source: 'unified-bridge',
        reason: 'timeout',
        projectCount: 0
      })
    } else {
      this.setLookupDebugInfo({
        source: 'unified-bridge',
        reason: result.error || 'empty',
        projectCount: 0
      })
    }

    // Fallback for hosts exposing direct eclipseBridge API
    const eclipseBridge = (
      window as Window & {
        eclipseBridge?: {
          getWorkspaceInfo?: () => Promise<{
            success: boolean
            projects?: Array<{ name?: string; path?: string }>
            workspacePath?: string
            name?: string
          }>
        }
      }
    ).eclipseBridge

    if (eclipseBridge?.getWorkspaceInfo) {
      try {
        const eclipseResult = await eclipseBridge.getWorkspaceInfo()
        if (eclipseResult?.success) {
          const projects = (eclipseResult.projects || [])
            .map(project =>
              this.toProject(project.name, project.path || eclipseResult.workspacePath)
            )
            .filter((project): project is Project => Boolean(project))

          if (projects.length > 0) {
            this.setLookupDebugInfo({
              source: 'eclipse-bridge',
              reason: 'ok',
              projectCount: projects.length
            })
            return projects
          }

          const rootOnlyProject = this.toProject(eclipseResult.name, eclipseResult.workspacePath)
          if (rootOnlyProject) {
            this.setLookupDebugInfo({
              source: 'eclipse-bridge',
              reason: 'root-path-fallback',
              projectCount: 1
            })
            return [rootOnlyProject]
          }
        }
      } catch {
        this.setLookupDebugInfo({
          source: 'eclipse-bridge',
          reason: 'exception',
          projectCount: 0
        })
      }
    }

    // Fallback for VSCode/Cursor webview bridge shim.
    if (typeof window.eclipseCall === 'function') {
      try {
        const raw = await window.eclipseCall('meta', 'getInfo', {})
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        const result = parsed as {
          success?: boolean
          data?: {
            projects?: Array<{ name?: string; path?: string }>
            workspaceFolders?: Array<{ name?: string; path?: string }>
            workspacePaths?: string[]
            workspacePath?: string
            name?: string
          }
        }

        if (result.success && result.data) {
          const projects = this.projectsFromMetaShape(result.data)
          this.setLookupDebugInfo({
            source: 'eclipse-call',
            reason: projects.length > 0 ? 'ok' : 'empty',
            projectCount: projects.length
          })
          return projects
        }
      } catch {
        this.setLookupDebugInfo({
          source: 'eclipse-call',
          reason: 'exception',
          projectCount: 0
        })
      }
    }

    this.setLookupDebugInfo({
      source: 'none',
      reason: typeof window.eclipseCall === 'function' ? 'all-empty' : 'no-eclipse-call',
      projectCount: 0
    })
    return []
  }

  async getProjectsWithTimeout(timeoutMs = 15000): Promise<Project[]> {
    return Promise.race<Project[]>([
      this.getProjects(),
      new Promise<Project[]>(resolve => {
        setTimeout(() => resolve([]), timeoutMs)
      })
    ])
  }

  async getProjectsWithRetry(options?: {
    attempts?: number
    retryDelayMs?: number
  }): Promise<Project[]> {
    const attempts = options?.attempts ?? 4
    const retryDelayMs = options?.retryDelayMs ?? 1200

    for (let attempt = 0; attempt < attempts; attempt++) {
      const projects = await this.getProjects()
      if (projects.length > 0) return projects

      const debug = this.getLastLookupDebugInfo()
      const shouldRetry =
        debug.reason === 'timeout' ||
        debug.reason === 'empty' ||
        debug.reason === 'all-empty' ||
        debug.reason === 'no-eclipse-call'
      if (!shouldRetry || attempt === attempts - 1) {
        return projects
      }

      await new Promise(resolve => setTimeout(resolve, retryDelayMs))
    }

    return []
  }

  getPreferredProjectPath(projects: Project[], preferredPath?: string): string {
    if (preferredPath && projects.some(project => project.path === preferredPath)) {
      return preferredPath
    }
    return projects[0]?.path || ''
  }

  async uploadProject(
    projectPath: string,
    projectName: string,
    workspaceUuid?: string
  ): Promise<UploadResult> {
    try {
      const filename = `${projectName}.tar.gz`
      const excludePatterns = ['*.class', '*.jar']
      const files = await collectArchiveFiles(projectPath, { excludePatterns })

      if (workspaceUuid) {
        return this.uploadProjectChunked(projectPath, projectName, workspaceUuid, filename, files)
      }

      return this.uploadProjectBase64(projectPath, projectName, filename, files)
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      }
    }
  }

  /**
   * Preferred upload path: write archive to temp file on disk, then read in
   * small chunks through the bridge and upload via XHR. This avoids passing
   * one giant base64 string through the SWT browser bridge which corrupts
   * data for large archives.
   */
  private async uploadProjectChunked(
    projectPath: string,
    projectName: string,
    workspaceUuid: string,
    filename: string,
    files: string[]
  ): Promise<UploadResult> {
    const CHUNK_SIZE = 2 * 1024 * 1024
    const tempPath = `${projectPath}/.curie-upload-${Date.now()}.tar.gz`

    try {
      const archiveInfo = await shell.createArchiveToFile(projectPath, files, tempPath, {
        prefix: projectName
      })

      const chunks: Uint8Array[] = []
      let offset = 0
      while (offset < archiveInfo.size) {
        const len = Math.min(CHUNK_SIZE, archiveInfo.size - offset)
        const base64Chunk = await shell.readBinaryChunk(tempPath, offset, len)
        const binaryString = atob(base64Chunk)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        chunks.push(bytes)
        offset += len
      }

      const blob = new Blob(chunks as BlobPart[], { type: 'application/gzip' })

      const clientType = 's3_upload'
      const presigned = await getPresignedUrl(
        `/api/workspace/${workspaceUuid}/org/upload/${clientType}/presigned-url`,
        filename,
        archiveInfo.size
      )

      await uploadToS3(blob, presigned)

      const s3Path = await notifyUploadComplete(
        `/api/workspace/${workspaceUuid}/org/upload/${clientType}/complete`,
        presigned.file_path
      )

      return {
        success: true,
        s3Path,
        fileCount: archiveInfo.fileCount,
        size: archiveInfo.size
      }
    } finally {
      try {
        await shell.deleteFile(tempPath)
      } catch {
        /* cleanup best-effort */
      }
    }
  }

  /** Fallback: read entire archive as base64 in one bridge call (fine for small archives or VSCode). */
  private async uploadProjectBase64(
    projectPath: string,
    projectName: string,
    filename: string,
    files: string[]
  ): Promise<UploadResult> {
    const archive = await shell.createArchive(projectPath, files, {
      prefix: projectName
    })

    const token = getPluginAccessToken()
    if (!token) {
      return { success: false, error: 'Not authenticated' }
    }

    const binaryString = atob(archive.base64Content)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const blob = new Blob([bytes], { type: 'application/gzip' })

    const formData = new FormData()
    formData.append('file', blob, filename)

    const baseUrl = bridge.getBaseUrl()
    const response = await fetch(`${baseUrl}/api/project/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return { success: false, error: extractApiErrorMessage(errorData, 'Upload failed') }
    }

    const data = (await response.json()) as Record<string, string>
    return {
      success: true,
      s3Path: data.s3_path,
      fileCount: archive.fileCount,
      size: archive.size
    }
  }

  async applyDiffs(
    diffs: DiffEntry[],
    projectPath: string
  ): Promise<{ success: boolean; applied: number; errors: string[] }> {
    const errors: string[] = []
    let applied = 0

    for (const diff of diffs) {
      try {
        const filePath = this.resolvePath(projectPath, diff.bFilePath || diff.aFilePath)

        switch (diff.diffType) {
          case 'ADD':
          case 'MODIFY':
            await bridge.call('fs', 'write', { path: filePath, content: diff.bFileContent })
            applied++
            break
          case 'DELETE':
            await bridge.call('fs', 'delete', { path: filePath })
            applied++
            break
          default:
            errors.push(`Unknown diff type: ${diff.diffType}`)
        }
      } catch (error) {
        errors.push(
          `Failed to apply ${diff.diffType} to ${diff.bFilePath}: ${error instanceof Error ? error.message : String(error)}`
        )
      }
    }

    return { success: errors.length === 0, applied, errors }
  }

  async undoDiffs(
    diffs: DiffEntry[],
    projectPath: string
  ): Promise<{ success: boolean; undone: number; errors: string[] }> {
    const errors: string[] = []
    let undone = 0

    for (const diff of diffs) {
      try {
        const filePath = this.resolvePath(projectPath, diff.aFilePath || diff.bFilePath)

        switch (diff.diffType) {
          case 'ADD':
            await bridge.call('fs', 'delete', { path: filePath })
            undone++
            break
          case 'MODIFY':
          case 'DELETE':
            await bridge.call('fs', 'write', { path: filePath, content: diff.aFileContent })
            undone++
            break
          default:
            errors.push(`Unknown diff type: ${diff.diffType}`)
        }
      } catch (error) {
        errors.push(
          `Failed to undo ${diff.diffType} to ${diff.aFilePath}: ${error instanceof Error ? error.message : String(error)}`
        )
      }
    }

    return { success: errors.length === 0, undone, errors }
  }

  async readFile(projectPath: string, relativePath: string): Promise<string | null> {
    try {
      const fullPath = this.resolvePath(projectPath, relativePath)
      const result = await bridge.call<{ content: string }>('fs', 'read', { path: fullPath })
      if (!result.success || !result.data) return null
      return result.data.content
    } catch {
      return null
    }
  }

  async writeFile(projectPath: string, relativePath: string, content: string): Promise<boolean> {
    try {
      const fullPath = this.resolvePath(projectPath, relativePath)
      const result = await bridge.call('fs', 'write', { path: fullPath, content })
      return result.success
    } catch {
      return false
    }
  }

  private resolvePath(basePath: string, relativePath: string): string {
    const cleanRelative = relativePath.replace(/^[^/]+\//, '')
    if (cleanRelative.startsWith('/')) return cleanRelative
    return `${basePath}/${cleanRelative}`
  }
}

export const projectService = new ProjectService()
